import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllGoals, getCurrentQuarter } from '../../services/goalsService';
import { getAllManagers } from '../../services/userService';
import { GoalDocument, RoleDocument } from '../../types';
import { 
  Building2,
  Search,
  Filter,
  Download,
  Users,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { generateGoalsDocx } from '../../services/docxService';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  initial_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  final_review: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Awaiting Review',
  initial_review: 'Under Review',
  approved: 'Approved',
  final_review: 'Final Review',
  completed: 'Completed',
};

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const [allGoals, setAllGoals] = useState<GoalDocument[]>([]);
  const [managers, setManagers] = useState<RoleDocument[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedManager, setSelectedManager] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedQuarter, setSelectedQuarter] = useState<number | 'all'>('all');
  
  const [showFilters, setShowFilters] = useState(false);
  
  const { quarter, year } = getCurrentQuarter();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [goals, mgrs] = await Promise.all([
          getAllGoals(),
          getAllManagers(),
        ]);
        setAllGoals(goals);
        setManagers(mgrs);
      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredGoals = allGoals.filter(goal => {
    const matchesSearch = searchTerm === '' || 
      goal.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      goal.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesManager = selectedManager === 'all' || goal.managerEmail === selectedManager;
    const matchesStatus = selectedStatus === 'all' || goal.status === selectedStatus;
    const matchesYear = selectedYear === 'all' || goal.year === selectedYear;
    const matchesQuarter = selectedQuarter === 'all' || goal.quarter === selectedQuarter;
    
    return matchesSearch && matchesManager && matchesStatus && matchesYear && matchesQuarter;
  });

  const years = [...new Set(allGoals.map(g => g.year))].sort((a, b) => b - a);

  const handleDownload = async (goal: GoalDocument) => {
    try {
      await generateGoalsDocx(goal);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  const goalsByManager = filteredGoals.reduce((acc, goal) => {
    const key = goal.managerEmail;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(goal);
    return acc;
  }, {} as Record<string, GoalDocument[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalGoals = allGoals.length;
  const currentQuarterGoals = allGoals.filter(g => g.quarter === quarter && g.year === year).length;
  const completedGoals = allGoals.filter(g => g.status === 'completed').length;
  const pendingReview = allGoals.filter(g => g.status === 'submitted').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Company Goals</h1>
        <p className="text-gray-500">View all employee goals across the organization</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Total Goals</p>
          <p className="text-2xl font-bold text-gray-900">{totalGoals}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Current Quarter</p>
          <p className="text-2xl font-bold text-blue-600">{currentQuarterGoals}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Pending Review</p>
          <p className="text-2xl font-bold text-yellow-600">{pendingReview}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-green-600">{completedGoals}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-gray-900">Filters</span>
          </div>
          {showFilters ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        
        {showFilters && (
          <div className="p-4 border-t bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                <select
                  value={selectedManager}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Managers</option>
                  {managers.map(mgr => (
                    <option key={mgr.email} value={mgr.email}>{mgr.displayName}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Years</option>
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Quarters</option>
                  <option value={1}>Q1</option>
                  <option value={2}>Q2</option>
                  <option value={3}>Q3</option>
                  <option value={4}>Q4</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">All Goals</h2>
            </div>
            <span className="text-sm text-gray-500">{filteredGoals.length} results</span>
          </div>
        </div>
        
        <div className="divide-y">
          {Object.entries(goalsByManager).map(([managerEmail, goals]) => {
            const manager = managers.find(m => m.email === managerEmail);
            return (
              <div key={managerEmail} className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {manager?.displayName || managerEmail}
                  </span>
                  <span className="text-sm text-gray-400">({goals.length} goals)</span>
                </div>
                <div className="space-y-2 ml-6">
                  {goals.map(goal => (
                    <div 
                      key={goal.id}
                      className="p-3 bg-gray-50 rounded-lg flex items-center justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{goal.userName}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[goal.status]}`}>
                            {statusLabels[goal.status]}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">Q{goal.quarter} {goal.year}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(goal)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                          title="Download DOCX"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/goals/${goal.id}`)}
                          className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          
          {filteredGoals.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No goals found matching your filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
